import { useState, useEffect, useCallback } from "react";

// ══ useCprj — hook V3 autonome ══
// Accès aux routes /api/v1/cprj/* (SCHEMA.1c — S190)
// Aucun import depuis le dossier v2 (architecture V3 isolée).
//
// Note : _getJwt() reproduit depuis useCahierV3.ts — aucune fonction JWT
// partagée n'est exportée dans v3/ (même pattern dans DiscussionWindow.tsx
// et LiveDiscussionView.tsx). Aucune extraction faite pour ne pas introduire
// une abstraction sur un seul usage.

const _BASE = "/api/v1/cprj";
const _KEY = import.meta.env.VITE_API_KEY || "missing-key";

function _getJwt(): string | null {
  try { return localStorage.getItem("ghostx-jwt"); } catch { return null; }
}

async function _apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const jwt = _getJwt();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-API-Key": _KEY,
    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
  };
  const res = await fetch(`${_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json() as Record<string, unknown>;
      if (body?.detail !== undefined) {
        const d = body.detail;
        if (typeof d === "string") {
          detail = `HTTP ${res.status} — ${d}`;
        } else if (Array.isArray(d)) {
          const msgs = (d as Array<{ msg?: string; loc?: unknown[] }>)
            .map((e) => {
              const loc = e.loc;
              const field = loc && loc.length > 0 ? String(loc[loc.length - 1]) : "?";
              return `${field}: ${e.msg ?? "erreur"}`;
            })
            .join(" · ");
          detail = `HTTP ${res.status} — ${msgs}`;
        } else {
          detail = `HTTP ${res.status} — ${JSON.stringify(d)}`;
        }
      }
    } catch { /* réponse non-JSON */ }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CprjSection {
  title: string;
  portee: "compte" | "dossier" | "projet";
}

export interface CprjChamp {
  id: string;
  section: string;
  portee: "compte" | "dossier" | "projet";
  libelle: string;
  types: string[];
  etapes: string[];
  source: string;
  qui: string[];
  rapports: string[];
  note_rapports: string;
  ajout: boolean;
}

export interface CprjStructure {
  sections: Record<string, CprjSection>;
  champs: CprjChamp[];
  vues: Record<string, string[]>;
}

export interface CprjValeur {
  id: number;
  champ_id: string;
  portee: string;
  valeur: unknown;
  niveau: "DEFAUT" | "DECLARE" | "CORRIGE" | "PROPOSE";
  provenance?: string | null; // NIVEAU.REGUL.1 — DEC-NIVEAUX-2AXES-1
  etat?: string | null;
  source: string;
  auteur_type: string;
  auteur_id: number | null;
  version: number;
  created_at: string;
}

export interface CprjVersion {
  id: number;
  vue: "RP" | "RS" | "DJ" | "SO" | "CC";
  numero: number;
  cree_par: number;
  created_at: string;
  chantier_id: number | null;
  projet_id: number | null;
  mission_id: number | null;
}

// ── Helpers query string ──────────────────────────────────────────────────────

interface _Qs {
  portee?: string;
  chantierId?: number | null;
  projetId?: number | null;
  missionId?: number | null;
  vue?: string;
}

function _buildQs(p: _Qs): string {
  const q = new URLSearchParams();
  if (p.portee)     q.set("portee",      p.portee);
  if (p.chantierId) q.set("chantier_id", String(p.chantierId));
  if (p.projetId)   q.set("projet_id",   String(p.projetId));
  if (p.missionId)  q.set("mission_id",  String(p.missionId));
  if (p.vue)        q.set("vue",         p.vue);
  const s = q.toString();
  return s ? `?${s}` : "";
}

// ── params communs ────────────────────────────────────────────────────────────

export interface CprjParams {
  portee: string;
  chantierId?: number | null;
  projetId?: number | null;
  missionId?: number | null;
}

// ── Fonctions exportées ───────────────────────────────────────────────────────

export async function getStructure(vue?: string): Promise<CprjStructure> {
  return _apiFetch<CprjStructure>(`/structure${vue ? `?vue=${encodeURIComponent(vue)}` : ""}`);
}

export async function getValeurs(params: CprjParams): Promise<CprjValeur[]> {
  return _apiFetch<CprjValeur[]>(`/valeurs${_buildQs(params)}`);
}

export interface PutValeurCorps {
  portee: string;
  valeur: unknown;
  niveau: "DECLARE" | "CORRIGE";
  source: string;
  chantier_id?: number | null;
  projet_id?: number | null;
  mission_id?: number | null;
}

export async function putValeur(
  champId: string,
  corps: PutValeurCorps,
): Promise<{ id: number; champ_id: string }> {
  return _apiFetch<{ id: number; champ_id: string }>(
    `/valeurs/${encodeURIComponent(champId)}`,
    { method: "PUT", body: JSON.stringify(corps) },
  );
}

export async function getHistorique(
  champId: string,
  params: { chantierId?: number | null; projetId?: number | null; missionId?: number | null },
): Promise<CprjValeur[]> {
  return _apiFetch<CprjValeur[]>(
    `/historique/${encodeURIComponent(champId)}${_buildQs(params)}`,
  );
}

export interface FigerCorps {
  vue: "RP" | "RS" | "DJ" | "SO" | "CC";
  chantier_id?: number | null;
  projet_id?: number | null;
  mission_id?: number | null;
}

export async function figerVersion(corps: FigerCorps): Promise<{ id: number }> {
  return _apiFetch<{ id: number }>("/versions", {
    method: "POST",
    body: JSON.stringify(corps),
  });
}

export async function listVersions(
  params: { chantierId?: number | null; projetId?: number | null; missionId?: number | null; vue?: string },
): Promise<CprjVersion[]> {
  return _apiFetch<CprjVersion[]>(`/versions${_buildQs(params)}`);
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface UseCprjParams {
  portee: string;
  chantierId?: number | null;
  projetId?: number | null;
  missionId?: number | null;
  vue?: string;
  /** Intervalle de polling en ms (0 = pas de polling). Défaut : 0. */
  pollMs?: number;
}

export interface UseCprjResult {
  structure: CprjStructure | null;
  valeurs: CprjValeur[];
  loading: boolean;
  error: string | null;
  ecrire: (champId: string, valeur: unknown, niveau: "DECLARE" | "CORRIGE", source: string) => Promise<void>;
  figer: (vue: "RP" | "RS" | "DJ" | "SO" | "CC") => Promise<{ id: number } | null>;
  recharger: () => void;
}

export function useCprj(params: UseCprjParams): UseCprjResult {
  const { portee, chantierId, projetId, missionId, vue, pollMs = 0 } = params;

  const [structure, setStructure] = useState<CprjStructure | null>(null);
  const [valeurs, setValeurs]     = useState<CprjValeur[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [tick, setTick]           = useState(0);

  const recharger = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getStructure(vue),
      getValeurs({ portee, chantierId, projetId, missionId }),
    ])
      .then(([s, v]) => {
        if (cancelled) return;
        setStructure(s);
        setValeurs(v);
        setError(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [portee, chantierId, projetId, missionId, vue, tick]);

  // Polling automatique (DEMO.CAHIER.LIVE.1)
  useEffect(() => {
    if (!pollMs || pollMs <= 0) return;
    const id = setInterval(recharger, pollMs);
    return () => clearInterval(id);
  }, [pollMs, recharger]);

  const ecrire = useCallback(
    async (champId: string, valeur: unknown, niveau: "DECLARE" | "CORRIGE", source: string) => {
      try {
        await putValeur(champId, {
          portee,
          valeur,
          niveau,
          source,
          chantier_id: chantierId ?? null,
          projet_id:   projetId   ?? null,
          mission_id:  missionId  ?? null,
        });
        recharger();
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [portee, chantierId, projetId, missionId, recharger],
  );

  const figer = useCallback(
    async (v: "RP" | "RS" | "DJ" | "SO" | "CC") => {
      try {
        return await figerVersion({
          vue: v,
          chantier_id: chantierId ?? null,
          projet_id:   projetId   ?? null,
          mission_id:  missionId  ?? null,
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      }
    },
    [chantierId, projetId, missionId],
  );

  return { structure, valeurs, loading, error, ecrire, figer, recharger };
}
